import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { CreditCard, Smartphone, Building2, Wallet, CheckCircle2, Loader2, Shield } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const methods = [
  { id: "upi", label: "UPI", desc: "GPay, PhonePe, Paytm UPI, BHIM", icon: Smartphone },
  { id: "card", label: "Debit / Credit Card", desc: "Visa, Mastercard, RuPay, Amex", icon: CreditCard },
  { id: "netbanking", label: "Net Banking", desc: "All major banks supported", icon: Building2 },
  { id: "wallet", label: "Paytm Wallet", desc: "Pay from your Paytm balance", icon: Wallet },
];

const PaymentDialog = ({ open, onClose, pkg }) => {
  const [method, setMethod] = useState("upi");
  const [stage, setStage] = useState("form"); // form | processing | success
  const [form, setForm] = useState({ name: "", email: "", phone: "", upi: "" });
  const { toast } = useToast();

  const reset = () => {
    setStage("form");
    setMethod("upi");
    setForm({ name: "", email: "", phone: "", upi: "" });
  };

  const close = () => {
    reset();
    onClose();
  };

  const pay = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast({ title: "Missing details", description: "Please complete required fields." });
      return;
    }
    setStage("processing");
    // Simulated Paytm gateway flow — MOCKED
    setTimeout(() => {
      setStage("success");
    }, 2200);
  };

  if (!pkg) return null;
  const advance = Math.round(pkg.price * 0.25);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden">
        {stage === "form" && (
          <div className="grid md:grid-cols-[1fr_280px]">
            <div className="p-6 md:p-8">
              <DialogHeader>
                <DialogTitle className="text-2xl" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Secure Checkout
                </DialogTitle>
                <DialogDescription>
                  Reserve your spot with a 25% advance. Balance payable before travel.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={pay} className="mt-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="name" className="text-xs">Full Name *</Label>
                    <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ravi Kumar" />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-xs">Phone *</Label>
                    <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 98765 43210" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email" className="text-xs">Email</Label>
                  <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" />
                </div>

                <div>
                  <Label className="text-xs">Payment Method</Label>
                  <RadioGroup value={method} onValueChange={setMethod} className="mt-2 grid grid-cols-2 gap-2">
                    {methods.map((m) => {
                      const Icon = m.icon;
                      return (
                        <Label
                          key={m.id}
                          htmlFor={m.id}
                          className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer transition ${
                            method === m.id ? "border-teal-600 bg-teal-50" : "border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          <RadioGroupItem value={m.id} id={m.id} className="mt-0.5" />
                          <div className="flex-1">
                            <div className="flex items-center gap-1.5 font-semibold text-sm text-slate-900">
                              <Icon className="w-3.5 h-3.5" /> {m.label}
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5">{m.desc}</div>
                          </div>
                        </Label>
                      );
                    })}
                  </RadioGroup>
                </div>

                {method === "upi" && (
                  <div>
                    <Label htmlFor="upi" className="text-xs">UPI ID</Label>
                    <Input id="upi" value={form.upi} onChange={(e) => setForm({ ...form, upi: e.target.value })} placeholder="yourname@paytm" />
                  </div>
                )}

                <Button type="submit" className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-base">
                  Pay ₹{advance.toLocaleString("en-IN")} via Paytm
                </Button>
                <div className="flex items-center justify-center gap-2 text-[11px] text-slate-500">
                  <Shield className="w-3 h-3" /> 100% Secure. PCI-DSS compliant · Powered by Paytm
                </div>
              </form>
            </div>

            <aside className="bg-slate-50 p-6 md:p-8 border-l border-slate-200">
              <div className="rounded-xl overflow-hidden mb-4">
                <img src={pkg.image} alt={pkg.title} className="w-full h-32 object-cover" />
              </div>
              <div className="text-xs font-semibold text-teal-700">{pkg.destination}</div>
              <h4 className="mt-1 font-bold text-slate-900 leading-snug">{pkg.title}</h4>
              <div className="mt-1 text-xs text-slate-500">{pkg.nights} Nights / {pkg.days} Days</div>

              <div className="mt-5 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Package price</span><span>₹{pkg.price.toLocaleString("en-IN")}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Advance (25%)</span><span className="font-semibold text-slate-900">₹{advance.toLocaleString("en-IN")}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Balance</span><span>₹{(pkg.price - advance).toLocaleString("en-IN")}</span></div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-200 flex justify-between font-bold">
                <span>Pay now</span>
                <span className="text-teal-700">₹{advance.toLocaleString("en-IN")}</span>
              </div>
            </aside>
          </div>
        )}

        {stage === "processing" && (
          <div className="p-12 text-center">
            <Loader2 className="w-14 h-14 text-teal-600 animate-spin mx-auto" />
            <h3 className="mt-6 text-xl font-bold text-slate-900">Connecting to Paytm...</h3>
            <p className="mt-2 text-sm text-slate-500">Do not close or refresh this window.</p>
          </div>
        )}

        {stage === "success" && (
          <div className="p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 mx-auto flex items-center justify-center">
              <CheckCircle2 className="w-9 h-9 text-green-600" />
            </div>
            <h3 className="mt-5 text-2xl font-bold text-slate-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Booking Confirmed!
            </h3>
            <p className="mt-2 text-slate-600">
              ₹{advance.toLocaleString("en-IN")} paid successfully. A confirmation has been sent to your phone & email.
            </p>
            <div className="mt-6 rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Booking ID</span><span className="font-mono font-semibold">PMR{Date.now().toString().slice(-8)}</span></div>
              <div className="flex justify-between mt-1"><span className="text-slate-500">Trip</span><span className="font-semibold">{pkg.title}</span></div>
            </div>
            <Button onClick={close} className="mt-6 bg-teal-600 hover:bg-teal-700">Done</Button>
            <p className="mt-4 text-[11px] text-orange-600 font-semibold">DEMO MODE — Paytm payment is MOCKED (no real transaction).</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PaymentDialog;
